import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Button } from "../../components/Button";
import { AppleComboBox } from "../../components/AppleComboBox";
import { apiGet, apiPost } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import { ymdTodayLocal } from "../../utils/dateUtils";
import { formatNumber, formatMoney } from "../../utils/formatNumber";
import { A } from "../../styles/appleClasses";
import { AppleDateInput } from "../../components/AppleDateInput";
import { AppleNumberInput } from "../../components/AppleNumberInput";
import MemoSection from "../../components/MemoSection";
import {
  extractApiDetailOutcome,
  isClosedPeriodError,
  isDateClosed,
} from "../../utils/closedPeriod";

/* ───────── Types ───────── */
type Account = {
  id: number | string;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

type AssetClassOption = {
  id: string | number;
  name: string;
  code?: string;
  default_life_months?: number;
};

type UsefulLifeOption = {
  id: string;
  name: string;
  years: number | null; // null = custom
};

type PaymentOption = {
  id: string;
  name: string;
  on_account: boolean;
  account: Account | null;
};

/* ───────── Utils ───────── */
const lower = (s?: string | null) =>
  String(s || "")
    .toLowerCase()
    .trim();
const accountDisplayName = (a?: Account | null) =>
  lower(a?.kind) === "credit_card_payable" ? "Credit Card" : a?.name || "";

const byName = <T extends { name?: string | null }>(a: T, b: T) =>
  (a.name ?? "").localeCompare(b.name ?? "", undefined, {
    sensitivity: "base",
    numeric: true,
  });

type TextEl = HTMLInputElement | HTMLTextAreaElement;

function selectAllOnFocus<T extends TextEl>(
  e: React.FocusEvent<T> | React.MouseEvent<T>,
) {
  const el = e.currentTarget;
  setTimeout(() => el.select(), 0);
}

function focusAndSelectOnMouseDown<T extends TextEl>(e: React.MouseEvent<T>) {
  const el = e.currentTarget;
  if (document.activeElement !== el) {
    e.preventDefault();
    el.focus();
    el.select();
  }
}

function money(n: number) {
  return formatMoney(n);
}

async function fetchBookDate(company_id: string): Promise<string> {
  const candidates = [
    `/book-date?company_id=${company_id}`,
    `/settings/book-date?company_id=${company_id}`,
    `/settings?company_id=${company_id}`,
  ];
  for (const url of candidates) {
    try {
      const res: any = await apiGet<any>(url);
      const d =
        typeof res === "string"
          ? res
          : res?.date || res?.book_date || res?.current_date || res?.today;
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    } catch {}
  }
  return ymdTodayLocal();
}

function buildPaymentOptions(accounts: Account[]): PaymentOption[] {
  return [
    ...accounts.map((a) => ({
      id: `account-${a.id}`,
      name: accountDisplayName(a),
      on_account: false,
      account: a,
    })),
    {
      id: "on_account",
      name: "On Account",
      on_account: true,
      account: null,
    },
  ];
}

type LocalErrorKey =
  | "newDeposit.invalidCompany"
  | "newFixedAsset.errors.selectPaymentOption"
  | "newFixedAsset.errors.selectPaymentSource"
  | "newFixedAsset.errors.dueDateRequired"
  | "newFixedAsset.errors.positiveCost"
  | "newFixedAsset.errors.assetNameRequired"
  | "newFixedAsset.errors.selectCategory"
  | "newFixedAsset.errors.selectUsefulLife"
  | "newFixedAsset.errors.enterUsefulLife"
  | "newFixedAsset.errors.closedFallback"
  | "newFixedAsset.errors.saveFallback"
  | "sales.errors.generic";

type PageError =
  | { kind: "key"; key: LocalErrorKey }
  | { kind: "raw"; text: string };

/* ───────── Page ───────── */
export default function NewFixedAsset() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id = "" } = useParams<{ company_id: string }>();

  const go = (path: string) => navigate(companyRoute(company_id, path));
  const backToList = () => go("/recognition/fixed-assets");

  const [assetName, setAssetName] = useState("");
  const [assetClasses, setAssetClasses] = useState<AssetClassOption[]>([]);
  const [assetClass, setAssetClass] = useState<AssetClassOption | null>(null);

  const [paymentAccounts, setPaymentAccounts] = useState<Account[]>([]);
  const [paymentOption, setPaymentOption] = useState<PaymentOption | null>(
    null,
  );

  const [cost, setCost] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(ymdTodayLocal());
  const [dueDate, setDueDate] = useState(ymdTodayLocal());
  const [memo, setMemo] = useState("");

  const LIFE_PRESETS = [1, 2, 3, 4, 5, 7, 10];
  const usefulLifeOptions: UsefulLifeOption[] = useMemo(() => {
    const xs: UsefulLifeOption[] = LIFE_PRESETS.map((y) => ({
      id: String(y),
      name: `${y} year${y > 1 ? "s" : ""}`,
      years: y,
    }));
    xs.push({ id: "custom", name: "Other…", years: null });
    return xs;
  }, []);

  const [lifeOpt, setLifeOpt] = useState<UsefulLifeOption | null>(
    usefulLifeOptions[0] || null,
  );
  const [customYears, setCustomYears] = useState<string>("");
  const [lifeTouched, setLifeTouched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<PageError | null>(null);
  const localError = (key: LocalErrorKey) => setError({ kind: "key", key });
  const errorText = error?.kind === "key" ? t(error.key) : error?.text || "";
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );
  const [payAccountsLoading, setPayAccountsLoading] = useState(true);

  const paymentOptions = useMemo(
    () => buildPaymentOptions(paymentAccounts),
    [paymentAccounts],
  );

  const isOnAccount = paymentOption?.on_account === true;
  const isPaidNow = !isOnAccount;
  const paymentAccount = paymentOption?.account ?? null;

  const categoryLabel = (opt: AssetClassOption) => {
    if (opt.code) {
      switch (opt.code) {
        case "computer": return t("newFixedAsset.classes.computer");
        case "leasehold": return t("newFixedAsset.classes.leasehold");
        case "equipment_fixtures": return t("newFixedAsset.classes.equipmentFixtures");
        case "other": return t("newFixedAsset.classes.other");
        default: return opt.name;
      }
    }
    switch (opt.name) {
      case "Fixed Asset – Computer": return t("newFixedAsset.kindAccounts.computer");
      case "Fixed Asset – Leasehold Improvements": return t("newFixedAsset.kindAccounts.leasehold");
      case "Fixed Asset – Equipment & Fixtures": return t("newFixedAsset.kindAccounts.equipmentFixtures");
      case "Fixed Asset – Other": return t("newFixedAsset.kindAccounts.other");
      default: return opt.name;
    }
  };
  const paymentLabel = (opt: PaymentOption) => opt.on_account
    ? t("sales.list.onAccount")
    : lower(opt.account?.kind) === "credit_card_payable"
      ? t("expenses.entry.creditCard") : opt.name;
  const lifeLabel = (opt: UsefulLifeOption) => opt.years == null
    ? t("newFixedAsset.otherLife")
    : opt.years === 1
      ? t("newFixedAsset.lifeYear_one", { count: opt.years })
      : t("newFixedAsset.lifeYear_other", { count: opt.years });

  const bodyCls = (A as any).body || "text-[13px] font-normal text-gray-700";
  const cardCls =
    (A as any).card || "bg-white border border-gray-200 rounded-2xl shadow-sm";
  const iconBtnCls =
    (A as any).iconBtn ||
    "h-10 w-10 inline-flex items-center justify-center rounded-xl hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-appleblueD";
  const inputBase =
    (A as any).input ||
    "w-full h-10 rounded-xl px-4 border border-gray-200 bg-white text-[13px] font-normal text-gray-700 " +
      "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue transition";
  const inputCls = `${inputBase} appearance-none`;
  const inputRightCls = `${inputCls} text-right tabular-nums`;
  const labelCls = "block text-xs text-gray-500 mb-1";

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!company_id) return;

      const qs = new URLSearchParams(location.search);
      const d = qs.get("date");
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        if (mounted) setPurchaseDate(d);
        return;
      }

      const srv = await fetchBookDate(company_id);
      if (mounted) setPurchaseDate(srv);
    })();

    return () => {
      mounted = false;
    };
  }, [company_id, location.search]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  useEffect(() => {
    if (isOnAccount && !dueDate) {
      setDueDate(ymdTodayLocal());
    }
  }, [isOnAccount, dueDate]);

  const refreshPaymentAccounts = async () => {
    if (!company_id) return;
    setPayAccountsLoading(true);
    try {
      const xs = await apiGet<Account[]>(`/accounts?company_id=${company_id}`);

      const allowed = new Set(["cash", "bank", "cashbank"]);
      const excluded = new Set([
        "cash_system",
        "bank_system",
        "cashbank_system",
      ]);

      const banks = (xs || [])
        .filter((a) => {
          if (
            lower(a.type) === "liability" &&
            lower(a.kind) === "credit_card_payable"
          )
            return true;
          if (a.is_system) return false;
          if (lower(a.type) !== "asset") return false;
          const kind = lower(a.kind);
          if (!allowed.has(kind)) return false;
          if (excluded.has(kind)) return false;
          return true;
        })
        .sort(byName);

      setPaymentAccounts(banks);

      const nextOptions = buildPaymentOptions(banks);
      setPaymentOption((prev) => {
        if (prev) {
          const matched = nextOptions.find((opt) => {
            if (opt.on_account !== prev.on_account) return false;
            if (opt.on_account) return true;
            return String(opt.account?.id) === String(prev.account?.id);
          });
          if (matched) return matched;
        }

        return (
          nextOptions.find((opt) => !opt.on_account) || nextOptions[0] || null
        );
      });
    } catch {
      setPaymentAccounts([]);
      setPaymentOption({
        id: "on_account",
        name: "On Account",
        on_account: true,
        account: null,
      });
    } finally {
      setPayAccountsLoading(false);
    }
  };

  const refreshAssetClasses = async () => {
    if (!company_id) return;

    try {
      const xs = await apiGet<any[]>(
        `/fixed-assets/classes?company_id=${company_id}`,
      );
      const list: AssetClassOption[] = (xs || [])
        .filter(
          (r) => r && typeof r.code === "string" && typeof r.label === "string",
        )
        .map((r) => ({
          id: String(r.code),
          code: String(r.code),
          name: String(r.label),
          default_life_months:
            typeof r.default_life_months === "number"
              ? r.default_life_months
              : undefined,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      if (list.length) {
        setAssetClasses(list);
        setAssetClass((prev) => {
          if (prev) {
            return (
              list.find((x) => String(x.id) === String(prev.id)) || list[0]
            );
          }
          return list[0];
        });
        return;
      }
    } catch {}

    try {
      const xs = await apiGet<any[]>(
        `/fixed-assets/kinds?company_id=${company_id}`,
      );
      const list: AssetClassOption[] = (xs || [])
        .filter((r) => r && r.id != null && typeof r.name === "string")
        .map((r) => ({ id: r.id, name: r.name }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setAssetClasses(list);
      setAssetClass((prev) => {
        if (prev) {
          return (
            list.find((x) => String(x.id) === String(prev.id)) ||
            list[0] ||
            null
          );
        }
        return list[0] || null;
      });
    } catch {
      setAssetClasses([]);
      setAssetClass(null);
    }
  };

  useEffect(() => {
    if (!company_id) return;

    refreshPaymentAccounts();
    refreshAssetClasses();

    const onFocus = () => {
      refreshPaymentAccounts();
      refreshAssetClasses();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  useEffect(() => {
    if (lifeTouched) return;

    const defMonths = assetClass?.default_life_months;
    if (typeof defMonths === "number" && defMonths > 0) {
      const yrs = Math.max(1, Math.ceil(defMonths / 12));
      const preset = usefulLifeOptions.find((o) => o.years === yrs);

      if (preset) {
        setLifeOpt(preset);
        setCustomYears("");
        return;
      }

      setLifeOpt(usefulLifeOptions.find((o) => o.id === "custom") || null);
      setCustomYears(String(yrs));
      return;
    }

    setLifeOpt(usefulLifeOptions[0] || null);
    setCustomYears("");
  }, [assetClass?.id, lifeTouched, usefulLifeOptions]);

  const usefulLifeYears = useMemo(() => {
    if (!lifeOpt) return 0;
    if (lifeOpt.years != null) return lifeOpt.years;
    const n = parseFloat(customYears);
    return isNaN(n) ? 0 : n;
  }, [lifeOpt, customYears]);

  const usefulLifeMonths = useMemo(() => {
    if (!usefulLifeYears || usefulLifeYears <= 0) return 0;
    return Math.max(1, Math.round(usefulLifeYears * 12));
  }, [usefulLifeYears]);

  const onCostBlur = () => {
    setCost(cost.trim());
  };

  const submit = async () => {
    setError(null);

    const c = parseFloat(String(cost || "").trim());
    const nm = assetName.trim();

    if (!company_id) return localError("newDeposit.invalidCompany");
    if (!paymentOption) return localError("newFixedAsset.errors.selectPaymentOption");
    if (isPaidNow && !paymentAccount) {
      return localError("newFixedAsset.errors.selectPaymentSource");
    }
    if (isOnAccount && !dueDate) {
      return localError("newFixedAsset.errors.dueDateRequired");
    }
    if (isNaN(c) || c <= 0) return localError("newFixedAsset.errors.positiveCost");
    if (!nm) return localError("newFixedAsset.errors.assetNameRequired");
    if (!assetClass) return localError("newFixedAsset.errors.selectCategory");
    if (!usefulLifeMonths || usefulLifeMonths <= 0) {
      return localError("newFixedAsset.errors.selectUsefulLife");
    }
    if (
      lifeOpt?.years == null &&
      (!customYears || parseFloat(customYears) <= 0)
    ) {
      return localError("newFixedAsset.errors.enterUsefulLife");
    }

    setSaving(true);
    try {
      const userMemo = (memo || "").trim();
      const purchaseAmount = Number(c.toFixed(2));
      const paidNowAccountId =
        isPaidNow && paymentAccount ? Number(paymentAccount.id) : null;

      const payload: any = {
        // dates / schedule
        purchase_date: purchaseDate,
        due_date: isOnAccount ? dueDate : null,
        schedule: {
          start_date: purchaseDate,
          useful_life_months: usefulLifeMonths,
        },

        // fixed asset core
        name: nm,
        asset_name: nm, // compatibility
        purchase_amount: purchaseAmount,
        amount: purchaseAmount, // compatibility
        on_account: isOnAccount,

        // payment
        purchase_bank_account_id: paidNowAccountId,
        bank_account_id: paidNowAccountId, // compatibility

        // class / account mapping
        asset_class_code: assetClass.code ?? null,

        // memo
        asset_memo: userMemo || null,
        memo: userMemo ? `Purchase ${nm} — ${userMemo}` : `Purchase ${nm}`,
      };

      if (!payload.asset_class_code) {
        payload.asset_account_id = Number(assetClass.id);
      }

      const res = await apiPost<any>(
        `/fixed-asset-purchases?company_id=${company_id}`,
        payload,
      );

      let assetId = Number(
        (res as any)?.asset_id ??
          (res as any)?.fixed_asset_id ??
          (res as any)?.asset?.id ??
          0,
      );

      if (!assetId) {
        const scheduleId = Number(
          (res as any)?.depreciation_schedule_id ??
            (res as any)?.schedule_id ??
            (res as any)?.id ??
            0,
        );

        if (scheduleId) {
          try {
            const sched = await apiGet<any>(
              `/depreciations/${scheduleId}?company_id=${company_id}`,
            );
            assetId = Number(sched?.asset_id ?? 0);
          } catch {}
        }
      }

      if (assetId) go(`/fixed-assets/${assetId}`);
      else backToList();
    } catch (e: any) {
      const outcome = extractApiDetailOutcome(e);
      // The helper's generic outcome was previously a nonempty English message.
      // Retain that precedence; do not expose the existing dormant fallbacks.
      const message: PageError = outcome.kind === "raw"
        ? { kind: "raw", text: outcome.text }
        : { kind: "key", key: "sales.errors.generic" };
      const hasMessage = outcome.kind === "generic" || !!outcome.text;

      if (isClosedPeriodError(e)) {
        if (hasMessage) setError(message);
        else localError("newFixedAsset.errors.closedFallback");
        return;
      }

      if (hasMessage) setError(message);
      else localError("newFixedAsset.errors.saveFallback");
    } finally {
      setSaving(false);
    }
  };

  const isClosedPurchaseDate = useMemo(() => {
    return isDateClosed(purchaseDate, closedThroughDate);
  }, [purchaseDate, closedThroughDate]);

  const canPost =
    !saving &&
    !!company_id &&
    !!paymentOption &&
    (!isPaidNow || !!paymentAccount) &&
    (!isOnAccount || !!dueDate) &&
    parseFloat(String(cost || "0")) > 0 &&
    !!assetName.trim() &&
    !!assetClass &&
    usefulLifeMonths > 0 &&
    !(lifeOpt?.years == null && (!customYears || parseFloat(customYears) <= 0));

  return (
    <div
      className={`min-h-[100dvh] bg-gray-50 ${bodyCls} flex flex-col items-center justify-start px-2 pt-6`}
    >
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={backToList}
          className={iconBtnCls}
          aria-label={t("directory.common.back")}
          title={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none">
          <h2 className="text-[15px] font-semibold text-gray-700 text-center">
            {t("spendingFixedAssets.new")}
          </h2>
        </div>

        <button
          type="button"
          onClick={backToList}
          className={iconBtnCls}
          aria-label={t("directory.common.close")}
          title={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <div className={`${cardCls} max-w-3xl w-full p-5 sm:p-6`}>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className={labelCls}>{t("fixedAssetEdit.assetName")}</label>
            <input
              type="text"
              className={inputCls}
              placeholder={t("fixedAssetEdit.assetNamePlaceholder")}
              value={assetName}
              onChange={(e) => setAssetName(e.target.value)}
              onFocus={selectAllOnFocus}
              onMouseDown={focusAndSelectOnMouseDown}
            />
          </div>

          <div>
            <label className={labelCls}>{t("expenses.common.category")}</label>
            <AppleComboBox<AssetClassOption>
              options={assetClasses}
              value={assetClass}
              onChange={setAssetClass}
              getOptionLabel={categoryLabel}
              getOptionSearchText={(opt) => `${opt.name} ${categoryLabel(opt)}`}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              placeholder={
                assetClasses.length ? t("fixedAssetEdit.selectCategory") : t("expenses.categories.noCategories")
              }
            />
            {!assetClasses.length ? (
              <div className="mt-2 text-xs text-gray-500">
                {t("newFixedAsset.categoriesTip")}
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>{t("directory.products.cost")}</label>
              <AppleNumberInput
                className={inputRightCls}
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                onBlur={onCostBlur}
                onFocus={selectAllOnFocus}
                onMouseDown={focusAndSelectOnMouseDown}
                placeholder="0"
                autoComplete="off"
              />
            </div>

            <div>
              <label className={labelCls}>{t("fixedAssetEdit.purchaseDate")}</label>

              <AppleDateInput
                valueYmd={purchaseDate}
                onChangeYmd={setPurchaseDate}
                ariaLabel={t("fixedAssetEdit.purchaseDate")}
                openCalendarLabel={t("sales.shared.openCalendar")}
                inputClassName={`${inputCls} tabular-nums appearance-none`}
                placeholder="MM/DD/YYYY"
              />

              {isClosedPurchaseDate && (
                <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {t("sales.errors.closedWarning")}
                </div>
              )}
            </div>

            <div>
              <label className={labelCls}>{t("newFixedAsset.usefulLife")}</label>
              <AppleComboBox<UsefulLifeOption>
                options={usefulLifeOptions}
                value={lifeOpt}
                onChange={(v) => {
                  setLifeTouched(true);
                  setLifeOpt(v);
                  if (v?.years != null) setCustomYears("");
                }}
                getOptionLabel={lifeLabel}
                getOptionSearchText={(opt) => `${opt.name} ${lifeLabel(opt)}`}
                preserveQueryOnDisplayChange
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
                placeholder={t("newFixedAsset.selectYears")}
              />

              {lifeOpt?.years == null && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    step="1"
                    className={"w-28 " + inputRightCls}
                    value={customYears}
                    onChange={(e) => {
                      setLifeTouched(true);
                      setCustomYears(e.target.value);
                    }}
                    onFocus={selectAllOnFocus}
                    onMouseDown={focusAndSelectOnMouseDown}
                    placeholder={t("newFixedAsset.yearsPlaceholder")}
                  />
                  <div className="text-xs text-gray-500">{t("newFixedAsset.yearsUnit")}</div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("sales.common.payment")}</label>
            <AppleComboBox<PaymentOption>
              options={paymentOptions}
              value={paymentOption}
              onChange={setPaymentOption}
              getOptionLabel={paymentLabel}
              getOptionSearchText={(opt) => `${opt.name} ${paymentLabel(opt)}`}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              placeholder={t("newFixedAsset.selectPayment")}
            />

            {!payAccountsLoading && !paymentAccounts.length ? (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2 border border-amber-100">
                {t("newFixedAsset.noPaymentAccounts")}
              </div>
            ) : null}
          </div>

          {isOnAccount ? (
            <div>
              <label className={labelCls}>{t("sales.common.dueDateAria")}</label>
              <AppleDateInput
                valueYmd={dueDate}
                onChangeYmd={setDueDate}
                ariaLabel={t("sales.common.dueDateAria")}
                openCalendarLabel={t("sales.shared.openCalendar")}
                inputClassName={`${inputCls} tabular-nums appearance-none`}
                placeholder="MM/DD/YYYY"
              />
            </div>
          ) : null}

          <MemoSection
            label={t("sales.common.memo")}
            placeholder={t("sales.common.memoPlaceholder")}
            mode="edit"
            value={memo}
            onChange={setMemo}
            className="mt-4 mb-8"
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={backToList} disabled={saving}>
            {t("directory.common.cancel")}
          </Button>
          <Button onClick={submit} disabled={!canPost}>
            {saving ? t("directory.common.savingEllipsis") : t("sales.common.enter")}
          </Button>
        </div>

        <div style={{ minHeight: 18 }} className="mt-2">
          {errorText ? (
            <div className="text-xs text-red-600 font-medium">{errorText}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
